// src/pages/use-cases/CreatorsPage.js
// Targets: YouTube dubbing, creator translation, influencer multilingual content
import UseCasePage from './UseCasePage';

const DATA = {
  slug: 'creators',
  breadcrumbLabel: 'Creators',
  seo: {
    title: 'AI Video Dubbing for Creators & YouTubers | DubLabs',
    description: 'Dub your YouTube videos into 32 languages without losing your voice. Grow global subscribers with DubLabs AI dubbing — no studio, no re-recording needed.',
  },
  eyebrow: 'For Creators',
  headline: 'Grow your channel in every language.',
  subhead: 'DubLabs clones your voice and dubs your videos into 32 languages so your Hindi, Spanish, or Japanese audiences hear the real you — not a stranger.',
  problem: 'Your best video hits a ceiling because it only speaks one language. Traditional dubbing costs $2,000+ per language and takes weeks. AI translation tools sound robotic and use a voice that isn\'t yours.',
  features: [
    {
      number: '01',
      title: 'Your actual voice, in every language',
      body: 'We clone your voice from a single video you send us. When your Hindi audience watches, they hear you — same tone, same emotion, same personality — speaking their language.',
    },
    {
      number: '02',
      title: 'Frame-accurate lip-sync',
      body: 'Your lips move in sync with the dubbed audio. No jarring mismatch that pulls viewers out of the video. We work on your existing footage, no re-shooting needed.',
    },
    {
      number: '03',
      title: 'Minutes, not weeks',
      body: 'Send us a 10-minute video, get it back dubbed into 5 languages in under 30 minutes of processing. Ship weekly to global audiences without a studio calendar.',
    },
    {
      number: '04',
      title: 'Per-video pricing you can predict',
      body: 'No monthly subscription trap, no credit meters that penalize long-form. Transparent pricing per video — quoted upfront based on length and languages, so you always know the cost before we start.',
    },
  ],
  scenarios: [
    {
      label: 'Scenario 01',
      headline: 'YouTube creator ships one video, gets 5 language versions the same day.',
      body: 'You film once in English and send it to us. Our team delivers Hindi, Spanish, Portuguese, French, and Arabic versions in parallel — each with your cloned voice and perfect lip-sync. Publish as separate videos or add multi-language audio tracks on YouTube. Your subscriber base compounds across markets you\'ve never posted in.',
    },
    {
      label: 'Scenario 02',
      headline: 'Course creator localizes their entire back catalog for the Indian market.',
      body: 'You have 50 hours of English coaching content. Traditional dubbing quote: $80,000. DubLabs dubs the whole catalog into Hindi and Tamil in 2 days at a fraction of the cost. Same voice throughout — the students bought your course, they hear you.',
    },
    {
      label: 'Scenario 03',
      headline: 'Podcast host repurposes long-form interviews for global reach.',
      body: 'A 90-minute interview goes out weekly. Our team handles the multi-hour dubbing that would strain a studio\'s calendar. You send us one master, we deliver the dubbed versions across markets, and you grow ad revenue on each localized audience.',
    },
  ],
  faqs: [
    {
      q: 'Will it actually sound like me, or generic?',
      a: 'We clone your voice from your original video. The dubbed output preserves your tone, cadence, and emotional inflection. It\'s not a generic TTS voice — it\'s you, in a different language. Send us a sample video and we\'ll dub it in a language of your choice for free so you can hear the result before committing to anything.',
    },
    {
      q: 'How does lip-sync work with my existing footage?',
      a: 'Our lip-sync AI adjusts mouth movements in your existing video to match the dubbed audio, frame-by-frame. You don\'t need to re-record anything. The output looks like you actually spoke the new language — no jarring lip-audio mismatch.',
    },
    {
      q: 'Which languages do you support for creators?',
      a: 'We ship in 32 languages including English, Hindi, Chinese, Spanish, Arabic, Portuguese, Japanese, French, German, Tamil, Bengali, Marathi, Telugu, and 19 more. See the full list on our /languages page.',
    },
    {
      q: 'How does pricing work for creators?',
      a: 'Per-video pricing quoted upfront — no monthly subscription. Volume discounts if you dub weekly. Contact us with your typical video length and target languages and we\'ll come back with a quote plus a free sample dub so you can hear the quality on your own content first.',
    },
    {
      q: 'Can I use dubbed videos on YouTube\'s multi-audio feature?',
      a: 'Yes. YouTube supports multiple audio tracks per video. We deliver outputs that plug directly in — one video with 5 language tracks, viewers pick their language, and analytics roll up to a single video. Cleaner than posting 5 separate videos.',
    },
  ],
};

export default function CreatorsPage() {
  return <UseCasePage {...DATA} />;
}
